import styles from "./by-day.module.css";

/**
 * **One day, one container** — the shape the patient file uses to group anything
 * that happened on a date.
 *
 * It started on the History tab, where the client's reading was *"I don't see
 * clear day separation"*: every day was the same 14px text as the rows beneath
 * it, so three visits read as one long list with three slightly bolder lines in
 * it. A card each is what makes "these happened together" visible without
 * reading a single date.
 *
 * **The date sits inside the container, on its left** (PF9, 2026-09-27): *"one
 * big container that contains the date and what was done on that day"*. It was a
 * shaded band across the top; on the left it costs no line of its own, and the
 * rows read against it the way a diary's do.
 *
 * **Shared rather than copied** — History, Notes, Imaging and Billing all answer
 * the same question, *what happened, and when*, so they answer it one way.
 */

export interface Day<T> {
  /** `YYYY-MM-DD`, and the sort key. */
  key: string;
  label: string;
  rows: T[];
}

/**
 * Bucket rows into days, newest day first, keeping each day's own order.
 *
 * `dateOf` returns the ISO date this row belongs to — which is rarely just
 * `createdAt`. A procedure belongs to the visit's day, an image to the day it
 * was captured, a note to the day it was written; each caller knows which of
 * its fields answers that and the ones behind it.
 */
export function groupByDay<T>(rows: readonly T[], dateOf: (row: T) => string): Day<T>[] {
  const days = new Map<string, T[]>();

  for (const row of rows) {
    const key = dateOf(row).slice(0, 10);
    const bucket = days.get(key);
    if (bucket === undefined) days.set(key, [row]);
    else bucket.push(row);
  }

  return [...days.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, bucket]) => ({ key, label: dayLabel(key), rows: bucket }));
}

/** `2026-09-06` → `Sunday, 6 September 2026`. */
export function dayLabel(iso: string): string {
  return format(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

/** `2026-09-06` → `6 Sept 2026` in the reader's locale. */
export function shortDay(iso: string): string {
  return format(iso, { day: "numeric", month: "short", year: "numeric" });
}

/** `2026-09-06` → `Sunday`. */
export function weekdayOf(iso: string): string {
  return format(iso, { weekday: "long" });
}

function format(iso: string, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { ...options, timeZone: "UTC" });
}

export interface DayCardProps {
  /** `YYYY-MM-DD` — the date drawn on the left. */
  dayKey: string;
  /** The day in full, for a screen reader: "Sunday, 6 September 2026". */
  label: string;
  /** What the date cell counts — "3 notes", "1 image". */
  count: string;
  children: React.ReactNode;
  /** Set when the rows inside bring their own padding, as a table does. */
  flush?: boolean;
  /** For the pager, which measures each day to know what fits (PF4). */
  measureRef?: (element: HTMLElement | null) => void;
  testId?: string;
}

/** One day's container: the date on the left, and what happened that day beside it. */
export function DayCard({
  dayKey,
  label,
  count,
  children,
  flush = false,
  measureRef,
  testId,
}: DayCardProps): React.JSX.Element {
  return (
    <li className={styles.day} ref={measureRef} data-day={dayKey} data-testid={testId}>
      <p className={styles.dayHead} aria-label={`${label}, ${count}`}>
        <span className={styles.dayDate}>{shortDay(dayKey)}</span>
        <span className={styles.dayWeekday}>{weekdayOf(dayKey)}</span>
        <span className={styles.dayCount}>{count}</span>
      </p>
      <div className={flush ? styles.bodyFlush : styles.body}>{children}</div>
    </li>
  );
}

/** The `<ol>` the containers sit in. */
export function DayList({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <ol className={styles.days} data-testid={testId}>
      {children}
    </ol>
  );
}

/** The space `DayList` draws between two days — the pager packs against it. */
export const DAY_GAP = 10;

/** `3 notes`, `1 image` — pluralised where the caller names the noun. */
export function counted(n: number, noun: string): string {
  return `${String(n)} ${noun}${n === 1 ? "" : "s"}`;
}
